import type { AppProps } from "next/app";
import Head from "next/head";

import { Provider } from "react-redux";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";

import Banner from "@components/Banner";
import UpgradeBanner from "@components/UpgradeBanner";

import "src/styles/globals.scss";
import "@rainbow-me/rainbowkit/styles.css";

import { getCustomTheme } from "../theme";

import { store } from "@store";
import RudderStackScript from "@components/RudderStackScript";
import AnalyticsMapping from "@constants/analytics_mapping.json";

import WagmiProvier from "@services/WagmiProvider";
import AppLayout from "@components/AppLayout/AppLayout";
import React, { useEffect, useState } from "react";
import { SanctionModal } from "@kelp-dao/kernel-design-system";
import useSanction from "@hooks/useSanction";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useAccount, useDisconnect } from "wagmi";
import { useDispatch } from "react-redux";
import { clearUserData } from "@store";
import useRegionCheck from "@hooks/useRegionCheck";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import PageLoader from "@components/PageLoader";
import { SentimintProvider } from "@kelp-dao/sentimint-widget-sdk";
import "@kelp-dao/sentimint-widget-sdk/style.css";
import { PrivateVaultModalContainer } from "@components/PrivateVaultModal";
import { checkPrivateVaultEligibility } from "@hooks/usePrivateVaultModal";

const colors = {
  myScheme: {
    50: "#daffff",
    100: "#b1fbfb",
    200: "#85f7f7",
    300: "#58f3f3",
    400: "#31f0f0",
    500: "#1ed7d7",
    600: "#0ca7a7",
    700: "#007777",
    800: "#004949",
    900: "#001a1a",
  },
};

export const theme = extendTheme({ colors });

// Blocked pages that require geo-location check
const BLOCKED_PAGES = [
  "/gain/growth-vault/",
  "/gain/airdrop-gain/",
  "/gain/stable-vault/",
];

const SENTIMINT_KEY = process.env.NEXT_PUBLIC_SENTIMINT_APY_KEY;

const DefaultHead = () => {
  return (
    <>
      {/* <Script
        src="https://attribution.metacrm.inc/tracking-1-2-0.js"
        data-entity-id="653b778fdad2d170612a7af9"
        id="metacrm-tracking"
        integrity="sha384-XXR5g7lSPTOllUzRbn8qgjH1nBfNmJ/wKfvyRO+7r4ldJxMGRCHpjY2jUm8mGsTr"
        crossOrigin="anonymous"
        // @ts-ignore
        apiKey="r7cd59xw9sa"
        ecoSystem="EVM"
      /> */}
      <RudderStackScript fileName={AnalyticsMapping["fileName"]} />
      <Head>
        <title>
          Kelp – The Leading Liquid Restaking Protocol | Restake Ethereum & Stay
          Liquid
        </title>
        <meta
          name="description"
          content="Kelp is a top restaking protocol with $2B+ TVL. Restake Ethereum (ETH) and other assets while maintaining liquidity with rsETH, live on 40+ DeFi platforms. Maximize your DeFi stake rewards today."
        />
        <meta name="robots" content="index, follow" />
      </Head>
    </>
  );
};

const SanctionModalContainer = () => {
  const { isSanctioned } = useSanction();
  const { isConnected, address } = useAccount();
  const { openConnectModal } = useConnectModal();
  const { disconnect } = useDisconnect();
  const dispatch = useDispatch();
  const [hasDismissed, setHasDismissed] = React.useState(false);
  const [shouldOpenConnect, setShouldOpenConnect] = React.useState(false);

  useEffect(() => {
    setHasDismissed(false);
  }, [address]);

  const handleClose = () => {
    setHasDismissed(true);
  };

  const handleConnectAnotherWallet = () => {
    setHasDismissed(true);
    setShouldOpenConnect(true);
    disconnect();
    dispatch(clearUserData());
  };

  useEffect(() => {
    if (shouldOpenConnect && !isConnected) {
      setShouldOpenConnect(false);
      openConnectModal?.();
    }
  }, [shouldOpenConnect, isConnected, openConnectModal]);

  return (
    <SanctionModal
      isOpen={Boolean(isConnected && isSanctioned && !hasDismissed)}
      onClose={handleClose}
      onConnectAnotherWallet={handleConnectAnotherWallet}
      discordLink="https://discord.com/invite/khSpqCFvGq/"
      telegramLink="https://t.me/KelpDAOxyz/"
    />
  );
};

function MyApp({ Component, pageProps }: AppProps) {
  const { showComponent, onLoopComplete } = useRegionCheck();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);

  const isBlockedPage = BLOCKED_PAGES.some((blockedPage) =>
    pathname?.startsWith(blockedPage),
  );

  // Check if private vault banner conditions are met (disables Sentimint)
  const isPrivateVaultBannerActive = checkPrivateVaultEligibility(
    pathname,
    searchParams?.get("utm_source") || null,
    searchParams?.get("utm_campaign") || null,
  );

  // Track navigation loading state
  useEffect(() => {
    const handleStart = (url: string) => {
      // Only show loader for blocked pages navigation
      if (BLOCKED_PAGES.some((blockedPage) => url.startsWith(blockedPage))) {
        setIsNavigating(true);
      }
    };

    const handleComplete = () => {
      setIsNavigating(false);
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, [router]);

  // Show loader during navigation to blocked pages OR during geo check
  const shouldShowLoader = isNavigating || (!showComponent && isBlockedPage);

  return (
    <>
      <DefaultHead />
      <SentimintProvider
        apiKey={SENTIMINT_KEY || ""}
        enabled={!isPrivateVaultBannerActive}
      >
        <ChakraProvider theme={getCustomTheme()}>
          <Provider store={store}>
            <WagmiProvier>
              <UpgradeBanner />
              <Banner variant={1} />
              <AppLayout>
                {shouldShowLoader ? (
                  <PageLoader onLoopComplete={onLoopComplete} />
                ) : !isBlockedPage ? (
                  <Component {...pageProps} suppressHydrationWarning />
                ) : showComponent ? (
                  <Component {...pageProps} suppressHydrationWarning />
                ) : (
                  <PageLoader onLoopComplete={onLoopComplete} />
                )}
              </AppLayout>
              <SanctionModalContainer />
              {!shouldShowLoader && isPrivateVaultBannerActive && (
                <PrivateVaultModalContainer />
              )}
            </WagmiProvier>
          </Provider>
        </ChakraProvider>
      </SentimintProvider>
    </>
  );
}

export default MyApp;
